/**
 * Load co-op per-class deposit from public site settings.
 */

import { useEffect, useState } from 'react';

import { usePublicSiteSettings } from '@contexts/PublicSiteSettingsContext';
import { siteSettingsApi } from '@services/siteSettingsApi';
import {
  classDepositEnrollNotice,
  classDepositHowToJoinDescription,
  DEFAULT_CLASS_DEPOSIT_CENTS,
  formatClassDepositUsd,
  normalizeClassDepositCents,
} from '@utils/classDeposit';

export function useClassDeposit(): {
  depositCents: number;
  depositFormatted: string;
  howToJoinDescription: string;
  enrollNotice: { title: string; body: string };
  /** True when a non-zero deposit is configured. */
  hasDeposit: boolean;
  loading: boolean;
} {
  const { settings, loading: settingsLoading } = usePublicSiteSettings();
  const [depositCents, setDepositCents] = useState(() =>
    normalizeClassDepositCents(settings?.classDepositCents)
  );
  // Without shared settings (and once they have stopped loading) this hook fetches its own copy.
  const [loading, setLoading] = useState(settings === null);

  // Follow the shared settings as they change (adjust state during render); the effect below
  // only runs the fallback fetch.
  const [prevShared, setPrevShared] = useState({ settings, settingsLoading });
  if (prevShared.settings !== settings || prevShared.settingsLoading !== settingsLoading) {
    setPrevShared({ settings, settingsLoading });
    if (settings) {
      setDepositCents(normalizeClassDepositCents(settings.classDepositCents));
      setLoading(false);
    } else if (!settingsLoading) {
      setLoading(true);
    }
  }

  useEffect(() => {
    if (settings || settingsLoading) return;

    let cancelled = false;
    siteSettingsApi
      .get(null)
      .then((res) => {
        if (cancelled) return;
        setDepositCents(normalizeClassDepositCents(res.data?.settings?.classDepositCents));
      })
      .catch(() => {
        if (!cancelled) setDepositCents(DEFAULT_CLASS_DEPOSIT_CENTS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [settings, settingsLoading]);

  const depositFormatted = formatClassDepositUsd(depositCents);

  return {
    depositCents,
    depositFormatted,
    howToJoinDescription: classDepositHowToJoinDescription(depositCents),
    enrollNotice: classDepositEnrollNotice(depositCents),
    hasDeposit: depositCents > 0,
    loading,
  };
}
